import { IListenerInfo, StandardEventsController } from "Global/Base/Utils/StandardEventsController";

export type HTMLListenerCallback = (event: Event) => unknown;

type HTMLListenerElement = HTMLElement | Window | Document | Element;

export type IHTMLListenerInfo = IListenerInfo & {
    listenerType: keyof HTMLElementEventMap | string;
    callback: HTMLListenerCallback;
    element: HTMLListenerElement;
};

class HTMLListenerInfo implements IHTMLListenerInfo {
    private _listenerType: keyof HTMLElementEventMap | string;
    private _callback: HTMLListenerCallback;
    private _element: HTMLListenerElement;
    private _options: EventListenerOptions;

    public get listenerType(): keyof HTMLElementEventMap | string {
        return this._listenerType;
    }

    public get callback(): HTMLListenerCallback {
        return this._callback;
    }

    public get element(): HTMLListenerElement {
        return this._element;
    }

    public get options(): EventListenerOptions {
        return this._options;
    }

    public key: string;

    constructor(listenerType: keyof HTMLElementEventMap | string, element: HTMLListenerElement, callback: HTMLListenerCallback, options: EventListenerOptions) {
        this._listenerType = listenerType;
        this._element = element;
        this._callback = callback;
        this._options = options;
    }

    public removeListener(options?: EventListenerOptions): void {
        this.element.removeEventListener(this.listenerType, this.callback, options || this.options);
        this._element = null;
        this._callback = null;
        this._options = null;
    }
}

export type EventsControllerAddListenerParams = {
    /** Standardní `options` metody `addEventListener`. */
    options?: AddEventListenerOptions;
};

export type EventsControllerAddListenerWithKeyParams = EventsControllerAddListenerParams & {
    /** Zároveň odváže všechny předchozí listenery se zadaným `key`. */
    removePrevious?: boolean;
};

/**
 * Slouží k navazování tesco eventů i DOM HTML listenerů. Pamatuje si všechny eventy a při dispose zase všechny odváže.
 * Pomáhá zabránit tomu, že někde zůstanou navázané eventy již disposnutých instancí.
 *
 * Také je možné navazovat eventy s unikátním klíčem `key` a kdykoliv odvázat jen eventy s daným klíčem.
 * @includeToDoc
 */
export class EventsController extends StandardEventsController {
    /**
     * Naváže listener na HTMLElement podle typu (přes `addEventListener`) a zapamatuje si jej.
     */
    public addListener<T extends keyof HTMLElementEventMap>(element: HTMLElement, type: T, callback: (this: HTMLElement, ev: HTMLElementEventMap[T]) => unknown, params?: EventsControllerAddListenerParams): IHTMLListenerInfo;
    public addListener(element: HTMLElement, type: string, callback: (this: HTMLElement, ev: Event) => unknown, params?: EventsControllerAddListenerParams): IHTMLListenerInfo;
    public addListener(element: HTMLElement, type: string, callback: EventListenerOrEventListenerObject, params?: EventsControllerAddListenerParams): IHTMLListenerInfo;
    public addListener<K extends keyof WindowEventMap>(window: Window, type: K, listener: (this: Window, ev: WindowEventMap[K]) => any, params?: EventsControllerAddListenerParams): IHTMLListenerInfo;
    public addListener(window: Window, type: string, listener: EventListenerOrEventListenerObject, params?: EventsControllerAddListenerParams): IHTMLListenerInfo;
    public addListener<T extends keyof DocumentEventMap>(document: Document, type: T, callback: (this: Document, ev: DocumentEventMap[T]) => unknown, params?: EventsControllerAddListenerParams): IHTMLListenerInfo;
    public addListener(document: Document, type: string, callback: EventListenerOrEventListenerObject, params?: EventsControllerAddListenerParams): IHTMLListenerInfo;
    public addListener<T extends keyof ElementEventMap>(element: Element, type: T, callback: (this: Element, ev: ElementEventMap[T]) => unknown, params?: EventsControllerAddListenerParams): IHTMLListenerInfo | null;
    public addListener(element: Element, type: string, callback: (this: Element, ev: Event) => unknown, params?: EventsControllerAddListenerParams): IHTMLListenerInfo | null;
    public addListener(element: Element, type: string, callback: EventListenerOrEventListenerObject, params?: EventsControllerAddListenerParams): IHTMLListenerInfo | null;
    public addListener<T extends keyof HTMLElementEventMap | string>(element: HTMLListenerElement, type: T, callback: HTMLListenerCallback, params?: EventsControllerAddListenerParams): IHTMLListenerInfo {
        if (!element || this.isDisposed)
            return null;
        element.addEventListener(type, callback, params?.options);
        const listenerInfo = new HTMLListenerInfo(type, element, callback, params?.options);
        this._listeners.push(listenerInfo);
        return listenerInfo;
    }

    /**
     * Naváže listener na HTMLElement podle typu (přes `addEventListener`) a zapamatuje si jej pod klíčem key.
     * @param key Unikátní klíč, pod kterým se listener/listenery uloží.
     */
    public addListenerWithKey<T extends keyof HTMLElementEventMap>(key: string, element: HTMLElement, type: T, callback: (this: HTMLElement, ev: HTMLElementEventMap[T]) => unknown, params?: EventsControllerAddListenerWithKeyParams): IHTMLListenerInfo;
    public addListenerWithKey(key: string, element: HTMLElement, type: string, callback: (this: HTMLElement, ev: Event) => unknown, params?: EventsControllerAddListenerWithKeyParams): IHTMLListenerInfo;
    public addListenerWithKey(key: string, element: HTMLElement, type: string, callback: EventListenerOrEventListenerObject, params?: EventsControllerAddListenerWithKeyParams): IHTMLListenerInfo;
    public addListenerWithKey<T extends keyof ElementEventMap>(key: string, element: Element, type: T, callback: (this: Element, ev: ElementEventMap[T]) => unknown, params?: EventsControllerAddListenerWithKeyParams): IHTMLListenerInfo | null;
    public addListenerWithKey(key: string, element: Element, type: string, callback: (this: Element, ev: Event) => unknown, params?: EventsControllerAddListenerWithKeyParams): IHTMLListenerInfo | null;
    public addListenerWithKey(key: string, element: Element, type: string, callback: EventListenerOrEventListenerObject, params?: EventsControllerAddListenerWithKeyParams): IHTMLListenerInfo | null;
    public addListenerWithKey<T extends keyof HTMLElementEventMap | string>(key: string, element: HTMLElement, type: T, callback: HTMLListenerCallback, params?: EventsControllerAddListenerWithKeyParams): IHTMLListenerInfo {
        if (params?.removePrevious)
            this.removeByKey(key);
        if (!element || this.isDisposed)
            return null;
        const listenerInfo = this.addListener(element, type, callback, params);
        listenerInfo.key = key;
        return listenerInfo;
    }

    /**
     * Odváže existující listener.
     */
    public removeListener<T extends keyof HTMLElementEventMap>(element: HTMLElement, type: T, callback: (this: HTMLElement, ev: HTMLElementEventMap[T]) => unknown, options?: EventListenerOptions): boolean;
    public removeListener(element: HTMLElement, type: string, callback: (this: HTMLElement, ev: Event) => unknown, options?: EventListenerOptions): boolean;
    public removeListener(element: HTMLElement, type: string, callback: EventListenerOrEventListenerObject, options?: EventListenerOptions): boolean;
    public removeListener<K extends keyof WindowEventMap>(window: Window, type: K, listener: (this: Window, ev: WindowEventMap[K]) => any, options?: EventListenerOptions): boolean;
    public removeListener(window: Window, type: string, listener: EventListenerOrEventListenerObject, options?: EventListenerOptions): boolean;
    public removeListener<T extends keyof DocumentEventMap>(document: Document, type: T, callback: (this: Document, ev: DocumentEventMap[T]) => unknown, options?: EventListenerOptions): boolean;
    public removeListener(document: Document, type: string, callback: EventListenerOrEventListenerObject, options?: EventListenerOptions): boolean;
    public removeListener<T extends keyof ElementEventMap>(element: Element, type: T, callback: (this: Element, ev: ElementEventMap[T]) => unknown, options?: EventListenerOptions): boolean;
    public removeListener(element: Element, type: string, callback: (this: Element, ev: Event) => unknown, options?: EventListenerOptions): boolean;
    public removeListener(element: Element, type: string, callback: EventListenerOrEventListenerObject, options?: EventListenerOptions): boolean;
    public removeListener<T extends keyof HTMLElementEventMap | string>(element: HTMLListenerElement, type: T, callback: HTMLListenerCallback, options?: EventListenerOptions): boolean {
        const listenerIndex = this._listeners.findIndex(listenerInfo => listenerInfo instanceof HTMLListenerInfo && listenerInfo.callback === callback && listenerInfo.element === element && listenerInfo.listenerType === type);
        if (listenerIndex < 0) {
            return false;
        }

        this._listeners[listenerIndex].removeListener(options);
        this._listeners.splice(listenerIndex, 1);

        return true;
    }
}
